import React, { useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import LadderBoard from './LadderBoard';
import { LadderIcon, EditIcon, TargetIcon, OrderIcon, BombIcon, PinIcon, renderLadderIcon, LadderIconKey } from './LadderIcons';
import { unlockSfx } from 'common/utils/sfx';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { IcClose } from 'components/common/icons/EmojiIcons';
import styles from './LadderGameModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface LadderParticipant {
    userNo: number;
    nickname: string;
}

export interface LadderGameModalProps {
    open: boolean;
    participants: LadderParticipant[];
    onClose: () => void;
}

type Stage = 'setup' | 'spinning' | 'result';

interface Preset {
    key: string;
    label: string;
    Icon: React.FC<{ size?: number; color?: string }>;
    resolve: (n: number) => Array<{ text: string; icon: LadderIconKey }>;
}

const PLACE_ICON_KEYS: LadderIconKey[] = ['cafe', 'beer', 'mic', 'home', 'dessert', 'game', 'bowling', 'taxi'];

const safeLen = (n: number) => Math.max(0, n);

// ⚠️ module-level 에서 i18n.t 를 호출하면 index.tsx 의 initI18n() 실행 전에 평가되어
// undefined 반환 → .map() 크래시로 SPA 전체 흰 화면. 반드시 사용 시점(getter)에서 호출.
const getPlaceIcons = (): { text: string; icon: LadderIconKey }[] => {
    const arr = i18n.t('games.ladderPlaces', { returnObjects: true });
    if (!Array.isArray(arr)) return [];
    return (arr as string[]).map((text, i) => ({ text, icon: PLACE_ICON_KEYS[i] }));
};

const getPresets = (): Preset[] => [
    { key: 'CUSTOM', label: i18n.t('games.customInput'), Icon: EditIcon, resolve: (n) => Array.from({ length: safeLen(n) }, (_, i) => ({ text: i18n.t('games.resultN', { n: i + 1 }), icon: 'edit' as LadderIconKey })) },
    { key: 'WHO_PAYS', label: i18n.t('games.whoPays'), Icon: TargetIcon, resolve: (n) => n < 1 ? [] : [{ text: i18n.t('games.leader'), icon: 'target' as LadderIconKey }, ...Array.from({ length: safeLen(n - 1) }, () => ({ text: i18n.t('games.normal'), icon: 'person' as LadderIconKey }))] },
    { key: 'ORDER', label: i18n.t('games.orderSet'), Icon: OrderIcon, resolve: (n) => Array.from({ length: safeLen(n) }, (_, i) => ({ text: i18n.t('games.numN', { n: i + 1 }), icon: 'order' as LadderIconKey })) },
    { key: 'PUNISH', label: i18n.t('games.punishDraw'), Icon: BombIcon, resolve: (n) => n < 1 ? [] : [{ text: i18n.t('games.blank'), icon: 'bomb' as LadderIconKey }, ...Array.from({ length: safeLen(n - 1) }, () => ({ text: i18n.t('games.pass'), icon: 'check' as LadderIconKey }))] },
    { key: 'NEXT_PLACE', label: i18n.t('games.nextPlace'), Icon: PinIcon, resolve: (n) => getPlaceIcons().slice(0, safeLen(n)) },
];

const LadderGameModal: React.FC<LadderGameModalProps> = ({ open, participants, onClose }) => {
    const { t } = useTranslation();
    // 기본은 전원 포함. excludedIds 에 있는 사람만 제외 (병돌리기와 동일 UX)
    const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
    const [presetKey, setPresetKey] = useState('WHO_PAYS');
    const [customResults, setCustomResults] = useState<string[]>([]);
    const [stage, setStage] = useState<Stage>('setup');
    const [seed, setSeed] = useState(1);
    const [mapping, setMapping] = useState<Array<{ startIdx: number; endIdx: number }>>([]);

    const activeNames = useMemo(() =>
        participants.filter(p => !excludedIds.has(p.userNo)).map(p => p.nickname),
        [participants, excludedIds]
    );

    // 언어 변경 시 라벨/결과 문구 재계산
    const presets = useMemo(() => getPresets(), [i18n.language]);
    const preset = presets.find(p => p.key === presetKey) || presets[0];
    const activeResults = useMemo<Array<{ text: string; icon: LadderIconKey }>>(() => {
        if (presetKey === 'CUSTOM') {
            return Array.from({ length: activeNames.length }, (_, i) => ({
                text: customResults[i] || i18n.t('games.resultN', { n: i + 1 }),
                icon: 'edit' as LadderIconKey,
            }));
        }
        return preset.resolve(activeNames.length);
    }, [preset, presetKey, activeNames.length, customResults]);

    useBackDismiss(open, onClose);
    if (!open) return null;

    const toggleParticipant = (userNo: number) => {
        setExcludedIds(prev => {
            const next = new Set(prev);
            if (next.has(userNo)) next.delete(userNo);
            else next.add(userNo);
            return next;
        });
    };

    const startSpin = () => {
        if (activeNames.length < 2) return;
        unlockSfx();
        setSeed(Date.now() >>> 0);
        setStage('spinning');
    };

    const restart = () => {
        setStage('setup');
        setMapping([]);
    };

    const handleFinish = (m: Array<{ startIdx: number; endIdx: number }>) => {
        setMapping(m);
        setTimeout(() => setStage('result'), 300);
    };

    return (
        <div className={cls("ladder-overlay")} onClick={stage !== 'spinning' ? onClose : undefined}>
            <div className={cls("ladder-modal")} onClick={e => e.stopPropagation()}>
                <header className={cls("ladder-header")}>
                    <span className={cls("ladder-header-title")}>
                        <LadderIcon size={22} color="#fff" />
                        <span>{t('games.ladderTitle')}</span>
                    </span>
                    {stage !== 'spinning' && <button className={cls("ladder-close")} onClick={onClose}><IcClose size={18} /></button>}
                </header>

                {stage === 'setup' && (
                    <>
                        <div className={cls("ladder-sec-title")}>1. 참여자 선택 <small>({activeNames.length}명)</small></div>
                        <ul className={cls("ladder-participants")}>
                            {participants.map(p => {
                                const included = !excludedIds.has(p.userNo);
                                return (
                                    <li key={p.userNo} className={cls(included ? 'sel' : '')}>
                                        <label>
                                            <input type="checkbox" checked={included} onChange={() => toggleParticipant(p.userNo)} />
                                            <span>{p.nickname}</span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className={cls("ladder-sec-title")}>2. 결과 선택</div>
                        <div className={cls("ladder-presets")}>
                            {presets.map(p => {
                                const IconComp = p.Icon;
                                return (
                                    <button
                                        key={p.key}
                                        className={cls(`ladder-preset-btn ${presetKey === p.key ? 'active' : ''}`)}
                                        onClick={() => setPresetKey(p.key)}
                                    >
                                        {/* 칩 글자색을 따라가게 — 고른 칩(검은 면)에서도 한 덩어리로 보입니다 */}
                                        <IconComp size={20} color="currentColor" />
                                        <span>{p.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {presetKey === 'CUSTOM' ? (
                            <div className={cls("ladder-custom")}>
                                {Array.from({ length: activeNames.length }).map((_, i) => (
                                    <input
                                        key={i}
                                        className={cls("ladder-custom-input")}
                                        placeholder={t('games.resultN', { n: i + 1 })}
                                        value={customResults[i] || ''}
                                        onChange={e => setCustomResults(prev => {
                                            const next = [...prev];
                                            next[i] = e.target.value;
                                            return next;
                                        })}
                                        maxLength={12}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className={cls("ladder-preview")}>
                                {activeResults.map((r, i) => (
                                    <span key={i} className={cls("ladder-preview-chip")}>
                                        {renderLadderIcon(r.icon, 14)}
                                        <span>{r.text}</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className={cls("ladder-actions")}>
                            <button className={cls("ladder-btn-cancel")} onClick={onClose}>{t('games.close')}</button>
                            <button className={cls("ladder-btn-spin")} onClick={startSpin} disabled={activeNames.length < 2}>
                                <LadderIcon size={18} color="#fff" /> 돌리기!
                            </button>
                        </div>
                    </>
                )}

                {(stage === 'spinning' || stage === 'result') && (
                    <>
                        <div className={cls("ladder-board-wrap")}>
                            <LadderBoard
                                names={activeNames}
                                results={activeResults}
                                seed={seed}
                                animating={stage === 'spinning'}
                                onFinish={handleFinish}
                                height={Math.max(10, activeNames.length * 3)}
                            />
                        </div>
                        {stage === 'result' && (
                            <>
                                <div className={cls("ladder-result-list")}>
                                    {mapping.map(m => {
                                        const res = activeResults[m.endIdx];
                                        return (
                                            <div key={m.startIdx} className={cls("ladder-result-row")}>
                                                <span className={cls("ladder-result-name")}>{activeNames[m.startIdx]}</span>
                                                <span className={cls("ladder-result-arrow")}>→</span>
                                                <span className={cls("ladder-result-value")}>
                                                    {renderLadderIcon(res.icon, 18)}
                                                    <span>{res.text}</span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className={cls("ladder-actions")}>
                                    <button className={cls("ladder-btn-cancel")} onClick={restart}>{t('games.retry')}</button>
                                    <button className={cls("ladder-btn-spin")} onClick={onClose}>{t('games.done')}</button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LadderGameModal;
